import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Palette } from 'lucide-react';

/** @param {string} raw */
function normalizeHexColor(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  const m = /^#?([0-9A-Fa-f]{6})$/.exec(s);
  if (!m) return '';
  return `#${m[1].toUpperCase()}`;
}

/**
 * AI vs custom hex colors with react-colorful (no native color input).
 *
 * @param {{
 *   source: 'ai' | 'custom';
 *   onSourceChange: (source: 'ai' | 'custom') => void;
 *   maxColors: number;
 *   selectedColors: string[];
 *   onSelectedColorsChange: (next: string[]) => void;
 *   onMarkTouched: () => void;
 *   aiChoiceHint?: string;
 *   groupAriaLabel: string;
 *   aiChoiceHintFirst?: boolean;
 *   showAiHint?: boolean;
 * }} props
 */
const DesignHexColorSection = forwardRef(function DesignHexColorSection({
  source,
  onSourceChange,
  maxColors,
  selectedColors,
  onSelectedColorsChange,
  onMarkTouched,
  aiChoiceHint = '',
  groupAriaLabel,
  aiChoiceHintFirst = false,
  showAiHint = true,
}, ref) {
  const [pickerTarget, setPickerTarget] = useState(
    /** @type {null | 'add' | number} */
    (null),
  );
  const [draftHex, setDraftHex] = useState('#888888');

  useEffect(() => {
    if (source === 'ai') {
      setPickerTarget(null);
    }
  }, [source]);

  useEffect(() => {
    if (typeof pickerTarget === 'number') {
      const h = normalizeHexColor(selectedColors[pickerTarget] || '');
      setDraftHex(h || '#888888');
    }
  }, [pickerTarget, selectedColors]);

  const openAdd = useCallback(() => {
    if (selectedColors.length >= maxColors) return;
    onMarkTouched();
    onSourceChange('custom');
    setDraftHex('#888888');
    setPickerTarget('add');
  }, [maxColors, onMarkTouched, onSourceChange, selectedColors.length]);

  const openEdit = useCallback(
    (index) => {
      onMarkTouched();
      const h = normalizeHexColor(selectedColors[index] || '');
      setDraftHex(h || '#888888');
      setPickerTarget(index);
    },
    [onMarkTouched, selectedColors],
  );

  const handlePickerChange = useCallback(
    (hex) => {
      const h = normalizeHexColor(hex);
      if (!h) return;
      setDraftHex(h);
      if (typeof pickerTarget === 'number') {
        onMarkTouched();
        const next = [...selectedColors];
        if (next[pickerTarget] !== undefined) {
          next[pickerTarget] = h;
          onSelectedColorsChange(next);
        }
      }
    },
    [onMarkTouched, onSelectedColorsChange, pickerTarget, selectedColors],
  );

  const handleAddConfirm = useCallback(() => {
    const h = normalizeHexColor(draftHex);
    if (!h || selectedColors.length >= maxColors) return;
    onMarkTouched();
    if (selectedColors.includes(h)) {
      setPickerTarget(null);
      return;
    }
    onSelectedColorsChange([...selectedColors, h].slice(0, maxColors));
    setPickerTarget(null);
  }, [draftHex, maxColors, onMarkTouched, onSelectedColorsChange, selectedColors]);

  const removeAt = useCallback(
    (index) => {
      onMarkTouched();
      const next = selectedColors.filter((_, i) => i !== index);
      onSelectedColorsChange(next);
      setPickerTarget(null);
      if (next.length === 0) {
        onSourceChange('ai');
      }
    },
    [onMarkTouched, onSelectedColorsChange, onSourceChange, selectedColors],
  );

  const selectAi = useCallback(() => {
    onMarkTouched();
    onSourceChange('ai');
    onSelectedColorsChange([]);
    setPickerTarget(null);
  }, [onMarkTouched, onSelectedColorsChange, onSourceChange]);

  const closePicker = useCallback(() => {
    setPickerTarget(null);
  }, []);

  useImperativeHandle(ref, () => ({
    flushPendingAdd() {
      if (pickerTarget !== 'add') return;
      const h = normalizeHexColor(draftHex);
      if (!h || selectedColors.length >= maxColors) {
        setPickerTarget(null);
        return;
      }
      if (!selectedColors.includes(h)) {
        onMarkTouched();
        onSelectedColorsChange([...selectedColors, h].slice(0, maxColors));
      }
      setPickerTarget(null);
    },
  }), [draftHex, maxColors, onMarkTouched, onSelectedColorsChange, pickerTarget, selectedColors]);

  const aiHintParagraph =
    showAiHint && source === 'ai' && aiChoiceHint ? (
      <p
        className={`new-design-hint new-design-hint--inline${
          aiChoiceHintFirst ? ' new-design-hint--above-chip-row' : ''
        }`}
      >
        {aiChoiceHint}
      </p>
    ) : null;

  return (
    <div className="new-design-hex-color-section">
      {aiChoiceHintFirst ? aiHintParagraph : null}
      <div
        className="new-design-mode-chip-row"
        role="group"
        aria-label={`${groupAriaLabel} mode`}
      >
        <button
          type="button"
          className={`new-design-mode-chip${source === 'ai' ? ' new-design-mode-chip--active' : ''}`}
          onClick={selectAi}
        >
          AI Choice
        </button>
        {selectedColors.map((hex, index) => (
          <div key={`swatch-${hex}-${index}`} className="new-design-color-swatch-wrap">
            <button
              type="button"
              className="new-design-color-dot new-design-color-dot--fill"
              style={{ backgroundColor: hex }}
              aria-label={`Custom color ${index + 1}`}
              onClick={() => openEdit(index)}
              title="Edit color"
            />
            <button
              type="button"
              className="new-design-color-remove"
              aria-label={`Remove color ${index + 1}`}
              onClick={() => removeAt(index)}
            >
              ×
            </button>
          </div>
        ))}
        {selectedColors.length < maxColors ? (
          <button
            type="button"
            className="new-design-mode-chip new-design-color-add-chip"
            aria-label="Add color"
            onClick={openAdd}
          >
            <Palette size={18} strokeWidth={2} aria-hidden="true" className="new-design-btn-icon" />
            {selectedColors.length > 0 ? 'add another color' : 'add your own color'}
          </button>
        ) : null}
      </div>
      {!aiChoiceHintFirst ? aiHintParagraph : null}
      {source === 'custom' ? (
        <>
          {pickerTarget !== null ? (
            <div className="new-design-hex-picker-panel">
              <HexColorPicker color={draftHex} onChange={handlePickerChange} />
              <div className="new-design-hex-picker-actions">
                {pickerTarget === 'add' ? (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={handleAddConfirm}>
                      Add color
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={closePicker}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn btn-ghost" onClick={closePicker}>
                    Done
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
});

export default DesignHexColorSection;
